import React, { useState, useEffect, useRef } from 'react';
import { string, func, bool, shape } from 'prop-types';
import { truncText, splitText } from 'src/util/PrioritizeUtil'
import { ReactComponent as Star } from 'src/images/star.svg'
import { ReactComponent as StarGray } from 'src/images/star_gray.svg'
import { ReactComponent as Edit } from 'src/images/edit.svg'
import { ReactComponent as Trash } from 'src/images/trash.svg'
import { ReactComponent as Add } from 'src/images/add.svg'
import './Factor.scss'
import { Button, DialogModalWindow } from 'src/components/shared'

const MAX_LENGTH = 80

/**
 * FACTOR on typescript implementing "Builder" pattern
 */


const FactorContainer = ({ children, style, className, ...other }:
  { children: any[], style?: object, className: string, }) => {
  return (
    <div className={`factor ${className || ''}`} style={style} {...other}>
      {children}
    </div>
  )
}

const InputFactor = ({ entry, onChange, idx }) => {
  const { description } = entry
  const liRef = useRef(null);
  const [rowsCount, setRowsCount] = useState(1)

  useEffect(() => {
    setRowsCount(Math.ceil(description.length / (liRef ? Math.ceil(liRef.current.offsetWidth / 7) : 1)))

  }, [liRef, description])

  return (
    <li ref={liRef} key={idx}>
      <span className="factor__index__editing">{5 - idx}</span>
      <span className="factor__index__editing">{(5 - idx) === 0 ? <StarGray /> : <Star />}</span>
      <textarea
        rows={rowsCount}
        className='factor__input'
        onChange={onChange(idx)}
        value={description}
      />
    </li>
  )

}

const InputFactors = ({ onChange, factor, onSave, onCancel }) => {
  const { description, title, responses } = factor

  const nameRef = useRef(null)
  useEffect(() => {
    nameRef.current.focus();
  }, [])

  return (
    <>
      <input
        ref={nameRef}
        className='factor__input'
        onChange={onChange('title')}
        value={title}
      />
      <input
        className='factor__input'
        onChange={onChange('description')}
        value={description}
      />
      <ul>
        {responses.map((entry, idx) => (
          <InputFactor key={idx} onChange={onChange} idx={idx} entry={entry} />
        ))}
      </ul>
        Weight: <input
        className='factor__input'
        type="number"
        min="1"
        onChange={onChange('weight')}
        value={factor.weight}
      />
      <div>
        <Button sm flat cancel
          onClick={onCancel}>
          Cancel
        </Button>
        <Button sm flat submit
          onClick={onSave}>
          Save
        </Button>
      </div>
    </>
  )

}


const SpanFactors = ({ readOnly = false, factor }) => {
  const { description, title, responses } = factor

  return (
    <>
      <p className="factor__title">{title} </p>
      <p>{description} </p>
      <ul>
        {responses.map(({ description }, idx) => (
          <li key={idx}>
            <span className="factor__index">{5 - idx}</span>
            {(5 - idx) === 0 ? <StarGray /> : <Star />}
            <span>{description} </span>
          </li>
        ))}
      </ul>
      {!readOnly && <span> Weight: {factor.weight}</span>}
    </>
  )

}

const Buttons = ({ readOnly, isEditMode, onDelete, toggleEditMode }) => {

  if (readOnly) return null

  if (isEditMode) return (
    <div className='factor__close'>
      <Trash onClick={onDelete} />
    </div>
  )

  return (
    <div className='factor__close'>
      <Edit onClick={toggleEditMode} />
      <Trash onClick={onDelete} />
    </div>
  )
}

export const AddFactor = ({ onClick: propOnClick }) => {
  return (
    <FactorContainer className='factor__add' onClick={propOnClick}>
      <div className="svg">
        <Add />
      </div>
      <div className="text">
        <b>Add New Factor</b>
      </div>
    </FactorContainer>
  )
}

const Factor = ({
  factor: propFactor,
  editable,
  onEdit: propOnEdit,
  onSave: propOnSave,
  onDelete: propOnDelete,
  readOnly = false
}) => {

  const truncFactorValues = (factor, truncResponses = true) => {
    const resFactor = { ...factor }
    Object.keys(resFactor).forEach(key => resFactor[key] = truncText(resFactor[key], MAX_LENGTH + 5))
    if (!resFactor.responses)
      resFactor.responses = []
    if (truncResponses)
      resFactor.responses = resFactor.responses
        .map(({ description, ...other }) => ({ description: truncText(description, MAX_LENGTH), ...other }))

    resFactor.responses = resFactor.responses.sort((a, b) => b.numeric_value - a.numeric_value)

    return resFactor
  };

  const [isEditMode, setIsEditMode] = useState(propFactor.is_draft)
  const [initialFactor,] = useState(truncFactorValues(propFactor))
  const [factor, setFactor] = useState(truncFactorValues(propFactor, false))
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode)
    if (propOnEdit) propOnEdit()
  }

  const onSave = () => {
    factor.weight = factor.weight <= 0 ? 1 : factor.weight
    setIsEditMode(!isEditMode)
    if (propOnSave) propOnSave(factor)
  }

  const onCancel = () => {
    const factorCopy = JSON.parse(JSON.stringify(initialFactor))
    setIsEditMode(false)
    setFactor(factorCopy)
  }

  const onDelete = () => {
    setIsDeleteModalOpen(false)
    if (propOnDelete) propOnDelete()
  }

  const onInput = (id) => ({ target }) => {
    const newFactor = { ...factor }
    if (typeof id === 'number') {
      const { description, ...other } = newFactor.responses[id]
      newFactor.responses[id] = { description: target.value, ...other }
    }
    if (id === 'weight') {
      if (target.value !== '' && target.value <= 0)
        newFactor[id] = 1
      else
        newFactor[id] = target.value
    }
    else
      newFactor[id] = target.value
    setFactor(newFactor)
  }

  const MODAL_TITLE_TEXT = 'Delete Rating Factor'
  const MODAL_TEXT = 'You can\'t undo this action.\nRatings submitted for this factor will be lost.'

  return (
    <FactorContainer className={readOnly && 'modal__factor'}>
      <DialogModalWindow
        isOpen={isDeleteModalOpen}
        title={MODAL_TITLE_TEXT}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={onDelete}
        confirmBtnText="Yes, delete factor"
        alert={true}
      >
        {splitText(MODAL_TEXT, '\n')}
      </DialogModalWindow>
      {editable &&
        <Buttons
          readOnly={readOnly}
          isEditMode={isEditMode}
          onDelete={() => setIsDeleteModalOpen(true)}
          toggleEditMode={toggleEditMode}
        />
      }
      {isEditMode
        ? (
          <InputFactors
            onChange={onInput}
            onSave={onSave}
            onCancel={onCancel}
            factor={factor}
          />
        ) : (
          <SpanFactors readOnly={readOnly} factor={truncFactorValues(factor)} />
        )
      }
    </FactorContainer>
  )

}

export default Factor


Factor.propTypes = {
  factor: shape({
    id: string,
    title: string,
    description: string
  }),
  add: bool,
  onClick: func,
};

Factor.defaultProps = {
  factor: {
    id: '',
    title: '',
    description: '',
    responses: []
  },
  add: false,
  onClick: () => { },
};